import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getKintoneIconUrl } from "~/config/kintoneIcons";
import type { ReverseEngineeringReportDetail, SelectedApp } from "~/interface/reverse";
import { updateEditableData } from "../../../slices/reverseEngineeringSlice";
import type { AppDispatch, RootState } from "../../../store";
import styles from "./index.module.scss";

interface RiskItem {
  level: "HIGH" | "MEDIUM" | "LOW";
  title: string;
  reason: string;
  recommendation: string;
  affectedFiles?: string[];
}

interface DependencyDetail {
  impact: "HIGH" | "MEDIUM" | "LOW" | "未確認";
  type: string;
  source: string;
  key: string;
  affectedFields: string;
  reason: string;
}

interface DependencySummary {
  referencingApps: number;
  referencedApps: number;
  detectedRelations: number;
  riskCount: number;
  codeFileCount: number;
  obfuscatedFileCount: number;
}

interface DependencyAnalysisResultProps {
  selectedApps?: SelectedApp[] | undefined;
  detailAppIds?: string[] | undefined;
  summary?: DependencySummary | undefined;
  risks?: RiskItem[] | undefined;
  dependencies?: DependencyDetail[] | undefined;
  onExport?: (() => void) | undefined;
  projectName?: string | undefined;
  documentId?: string | undefined;
  report?: ReverseEngineeringReportDetail | null | undefined;
}

const DependencyAnalysisResult: React.FC<DependencyAnalysisResultProps> = ({
  selectedApps: rawSelectedApps = [],
  detailAppIds,
  summary,
  risks = [],
  dependencies = [],
  report,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isEditable, editableData } = useSelector((state: RootState) => state.reverseEngineering);
  const isEditMode = isEditable.dependency;
  const dependencyEditableData = editableData.dependency;
  const [appIcons, setAppIcons] = useState<Record<string, string>>({});

  const normalizedDetailAppIds = useMemo(() => {
    if (!detailAppIds || detailAppIds.length === 0) {
      return null;
    }
    return new Set(detailAppIds.map((id) => String(id)));
  }, [detailAppIds]);

  const selectedApps = useMemo(() => {
    if (!rawSelectedApps || rawSelectedApps.length === 0) {
      return [] as SelectedApp[];
    }

    if (normalizedDetailAppIds && normalizedDetailAppIds.size > 0) {
      return rawSelectedApps.filter((app) => {
        const appId = app.id ?? app.appId;
        return appId ? normalizedDetailAppIds.has(String(appId)) : false;
      });
    }

    const flagged = rawSelectedApps.filter((app: any) => app?.isDetailAnalysisTarget);
    return flagged.length ? flagged : rawSelectedApps;
  }, [rawSelectedApps, normalizedDetailAppIds]);
  // Helper function to update editable data
  const updateEditableSummary = (field: keyof DependencySummary, value: number) => {
    if (!dependencyEditableData) return;

    const updatedData = {
      ...dependencyEditableData,
      summary: {
        ...dependencyEditableData.summary,
        [field]: value,
      },
    };

    dispatch(updateEditableData({ documentType: "dependency", data: updatedData }));
  };

  // データがない場合のデフォルト値
  const displaySummary =
    isEditMode && dependencyEditableData?.summary
      ? dependencyEditableData.summary
      : summary || {
          referencingApps: 0,
          referencedApps: 0,
          detectedRelations: 0,
          riskCount: 0,
          codeFileCount: 0,
          obfuscatedFileCount: 0,
        };

  useEffect(() => {
    if (!selectedApps.length) {
      setAppIcons({});
      return;
    }

    const iconMap: Record<string, string> = {};

    selectedApps.forEach((app) => {
      const appId = app.id ?? app.appId;
      if (!appId) {
        return;
      }

      const iconUrl = app.iconUrl;
      const iconKeyRaw =
        app.iconFileKey ??
        report?.kintone_apps?.find((item) => item.appId === appId)?.iconFileKey ??
        null;

      if (iconUrl) {
        iconMap[appId] = iconUrl;
        return;
      }

      if (!iconKeyRaw) {
        return;
      }

      if (/^https?:\/\//i.test(iconKeyRaw)) {
        iconMap[appId] = iconKeyRaw;
        return;
      }

      const preset = getKintoneIconUrl(iconKeyRaw);
      if (preset) {
        iconMap[appId] = preset;
      }
    });

    setAppIcons(iconMap);
  }, [selectedApps, report?.kintone_apps]);

  const getLevelColor = (level: string) => {
    switch (level) {
      case "HIGH":
        return styles["high"];
      case "MEDIUM":
        return styles["medium"];
      case "LOW":
        return styles["low"];
      default:
        return "";
    }
  };

  return (
    <div className={styles["resultContainer"]} data-export-root="reverse-engineering">
      {/* Selected Apps */}
      <div className={styles["selectedAppsSection"]}>
        <span className={styles["label"]}>対象アプリ</span>
        <div className={styles["selectedApps"]}>
          {selectedApps.map((app) => {
            const appId = app.id || "";
            return (
              <div key={appId} className={styles["appItem"]}>
                <div
                  className={styles["appIcon"]}
                  style={
                    appId && appIcons[appId]
                      ? { backgroundImage: `url(${appIcons[appId]})` }
                      : undefined
                  }
                />
                <span className={styles["appName"]}>{app.name}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Cards */}
      <div className={styles["summaryCards"]}>
        <div className={styles["summaryCard"]}>
          <div className={styles["cardLabel"]}>検出した関係（件）</div>
          {isEditMode && dependencyEditableData ? (
            <input
              type="number"
              className={styles["editableInput"]}
              value={displaySummary.detectedRelations}
              onChange={(e) =>
                updateEditableSummary("detectedRelations", parseInt(e.target.value, 10) || 0)
              }
            />
          ) : (
            <div className={styles["cardValue"]}>{displaySummary.detectedRelations}</div>
          )}
        </div>
        <div className={styles["summaryCard"]}>
          <div className={styles["cardLabel"]}>リスク件数</div>
          {isEditMode && dependencyEditableData ? (
            <input
              type="number"
              className={styles["editableInput"]}
              value={displaySummary.riskCount}
              onChange={(e) =>
                updateEditableSummary("riskCount", parseInt(e.target.value, 10) || 0)
              }
            />
          ) : (
            <div className={styles["cardValue"]}>{displaySummary.riskCount}</div>
          )}
        </div>
        <div className={styles["summaryCard"]}>
          <div className={styles["cardLabel"]}>カスタマイズファイル数</div>
          {isEditMode && dependencyEditableData ? (
            <input
              type="number"
              className={styles["editableInput"]}
              value={displaySummary.codeFileCount}
              onChange={(e) =>
                updateEditableSummary("codeFileCount", parseInt(e.target.value, 10) || 0)
              }
            />
          ) : (
            <div className={styles["cardValue"]}>{displaySummary.codeFileCount}</div>
          )}
        </div>
        <div className={styles["summaryCard"]}>
          <div className={styles["cardLabel"]}>難読化ファイル数</div>
          {isEditMode && dependencyEditableData ? (
            <input
              type="number"
              className={styles["editableInput"]}
              value={displaySummary.obfuscatedFileCount}
              onChange={(e) =>
                updateEditableSummary("obfuscatedFileCount", parseInt(e.target.value, 10) || 0)
              }
            />
          ) : (
            <div className={styles["cardValue"]}>{displaySummary.obfuscatedFileCount}</div>
          )}
        </div>
      </div>

      {/* Risk Items */}
      <div className={styles["section"]}>
        <h3 className={styles["sectionTitle"]}>要注意の関係</h3>
        {Array.isArray(risks) && risks.length > 0 ? (
          <div className={styles["riskGrid"]}>
            {risks.map((risk, index) => {
              // リスクデータの検証
              if (!risk || typeof risk !== "object") {
                console.error("Invalid risk data:", risk);
                return null;
              }

              return (
                <div key={index} className={styles["riskCard"]}>
                  <div className={styles["riskHeader"]}>
                    <span
                      className={`${styles["riskLevel"]} ${getLevelColor(risk.level || "LOW")}`}
                    >
                      {risk.level || "UNKNOWN"}
                    </span>
                    <h4 className={styles["riskTitle"]}>{risk.title || "タイトル未設定"}</h4>
                  </div>
                  <div className={styles["riskContent"]}>
                    <p>
                      理由：{(() => {
                        const reason = risk.reason || "詳細情報なし";
                        const targetLabel = "対象ファイル:";
                        const targetLabelAlt = "対象ファイル：";
                        const targetIndex =
                          reason.indexOf(targetLabel) !== -1
                            ? reason.indexOf(targetLabel)
                            : reason.indexOf(targetLabelAlt);

                        if (
                          risk.affectedFiles &&
                          risk.affectedFiles.length > 0 &&
                          targetIndex !== -1
                        ) {
                          const labelUsed =
                            reason.indexOf(targetLabel) !== -1 ? targetLabel : targetLabelAlt;
                          const fullFileList = risk.affectedFiles
                            .filter((fileName): fileName is string => Boolean(fileName))
                            .join(", ");
                          // fullFileListが空でない場合のみ置換する
                          if (fullFileList) {
                            return `${reason.slice(0, targetIndex + labelUsed.length)} ${fullFileList}`;
                          }
                        }
                        return reason;
                      })()}
                    </p>
                    <p>推奨：{risk.recommendation || "推奨事項なし"}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : !Array.isArray(risks) ? (
          <div className={styles["errorMessage"]}>
            <p>リスクデータの形式が不正です。データを確認してください。</p>
          </div>
        ) : (
          <p className={styles["noDataMessage"]}>リスクは検出されませんでした。</p>
        )}
      </div>

      {/* Dependency Details Table */}
      <div className={styles["section"]}>
        <h3 className={styles["sectionTitle"]}>関係の詳細</h3>
        {Array.isArray(dependencies) && dependencies.length > 0 ? (
          <div className={styles["table"]}>
            <div className={styles["tableHeader"]}>
              <div
                className={styles["th"]}
                style={{
                  width: "50px",
                  minWidth: "50px",
                  maxWidth: "50px",
                  justifyContent: "center",
                }}
              >
                No.
              </div>
              <div
                className={styles["th"]}
                style={{
                  width: "100px",
                  minWidth: "100px",
                  maxWidth: "100px",
                  justifyContent: "center",
                }}
              >
                影響度
              </div>
              <div
                className={styles["th"]}
                style={{ width: "140px", minWidth: "140px", maxWidth: "140px" }}
              >
                関係タイプ
              </div>
              <div className={styles["th"]}>参照元アプリ→参照先アプリ</div>
              <div className={styles["th"]}>キー/条件</div>
              <div className={styles["th"]}>影響フィールド</div>
              <div className={styles["th"]}>理由</div>
            </div>
            <div className={styles["tableBody"]}>
              {[...dependencies]
                .sort((a, b) => {
                  const impactOrder: Record<string, number> = {
                    HIGH: 0,
                    MEDIUM: 1,
                    LOW: 2,
                    未確認: 3,
                  };
                  const aOrder = impactOrder[a.impact] ?? 4;
                  const bOrder = impactOrder[b.impact] ?? 4;
                  return aOrder - bOrder;
                })
                .map((dep, index) => {
                  // 依存関係データの検証
                  if (!dep || typeof dep !== "object") {
                    console.error("Invalid dependency data:", dep);
                    return null;
                  }

                  return (
                    <div key={index} className={styles["tableRow"]}>
                      <div
                        className={styles["td"]}
                        style={{
                          width: "50px",
                          minWidth: "50px",
                          maxWidth: "50px",
                          justifyContent: "center",
                        }}
                      >
                        {dep.impact === "未確認" || !dep.impact ? "-" : index + 1}
                      </div>
                      <div
                        className={styles["td"]}
                        style={{
                          width: "100px",
                          minWidth: "100px",
                          maxWidth: "100px",
                          justifyContent: "center",
                        }}
                      >
                        <span
                          className={`${styles["riskLevel"]} ${getLevelColor(dep.impact || "LOW")}`}
                        >
                          {dep.impact || "UNKNOWN"}
                        </span>
                      </div>
                      <div
                        className={styles["td"]}
                        style={{ width: "140px", minWidth: "140px", maxWidth: "140px" }}
                      >
                        {dep.type || "不明"}
                      </div>
                      <div className={styles["td"]}>{dep.source || "不明"}</div>
                      <div className={styles["td"]}>{dep.key || "-"}</div>
                      <div className={styles["td"]}>{dep.affectedFields || "-"}</div>
                      <div className={styles["td"]}>{dep.reason || "-"}</div>
                    </div>
                  );
                })}
            </div>
          </div>
        ) : !Array.isArray(dependencies) ? (
          <div className={styles["errorMessage"]}>
            <p>依存関係データの形式が不正です。データを確認してください。</p>
          </div>
        ) : (
          <p className={styles["noDataMessage"]}>依存関係は検出されませんでした。</p>
        )}
      </div>
    </div>
  );
};

export default DependencyAnalysisResult;
